import React, { useState, useEffect } from 'react';
import {
  Card, Button, Input, AutoComplete, DatePicker, TimePicker, message, Alert,
  Row, Col, Empty, Checkbox, Tooltip, Select, Divider, Modal, Form
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../../utils/auth';
import dayjs from 'dayjs';
import { searchAirports, findAirportByCode, formatAirportCn, formatAirportEn } from '../../utils/airports';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

// 新西兰飞往中国的常用航司（默认下拉选项，和航司库合并去重）
const NZ_CHINA_AIRLINES = [
  { name: '新西兰航空 Air New Zealand', code: 'NZ' },
  { name: '中国国际航空 Air China', code: 'CA' },
  { name: '中国东方航空 China Eastern', code: 'MU' },
  { name: '中国南方航空 China Southern', code: 'CZ' },
  { name: '厦门航空 Xiamen Airlines', code: 'MF' },
  { name: '四川航空 Sichuan Airlines', code: '3U' },
  { name: '海南航空 Hainan Airlines', code: 'HU' },
  { name: '天津航空 Tianjin Airlines', code: 'GS' },
  { name: '首都航空 Beijing Capital Airlines', code: 'JD' },
  { name: '国泰航空 Cathay Pacific', code: 'CX' },
];

const StepFlightInfo = ({ tour, tourNotice, onRefresh }) => {
  const [flights, setFlights] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dbAirlines, setDbAirlines] = useState([]);
  const [addAirlineVisible, setAddAirlineVisible] = useState(false);
  const [addingAirline, setAddingAirline] = useState(false);
  const [airlineForm] = Form.useForm();

  useEffect(() => {
    fetchAirlines();
  }, []);

  const fetchAirlines = async () => {
    try {
      const res = await axios.get(`${API_BASE}/airlines`, { headers: AuthService.getAuthHeaders() });
      if (res.data.success) setDbAirlines(res.data.data || []);
    } catch (err) {
      console.error('获取航司列表失败:', err);
    }
  };

  // 默认 NZ→中国航司 + 航司库合并去重（按 IATA 代码）
  const airlineOptions = (() => {
    const seen = new Set();
    const merged = [];
    [...NZ_CHINA_AIRLINES, ...dbAirlines.filter(a => a.isActive !== false).map(a => ({ name: a.name, code: a.code }))]
      .forEach(a => {
        const key = (a.code || a.name).toUpperCase();
        if (seen.has(key)) return;
        seen.add(key);
        merged.push({ value: a.name, label: a.code ? `${a.name}（${a.code}）` : a.name });
      });
    return merged;
  })();

  const handleAddAirline = async () => {
    let values;
    try {
      values = await airlineForm.validateFields();
    } catch (e) {
      return;
    }
    try {
      setAddingAirline(true);
      const res = await axios.post(`${API_BASE}/airlines`, values, { headers: AuthService.getAuthHeaders() });
      if (res.data.success) {
        message.success('航司添加成功');
        setAddAirlineVisible(false);
        airlineForm.resetFields();
        fetchAirlines();
      }
    } catch (err) {
      message.error('添加失败: ' + (err.response?.data?.message || err.response?.data?.error || err.message));
    } finally {
      setAddingAirline(false);
    }
  };

  useEffect(() => {
    if (tourNotice?.flightInfo) {
      const addDefaults = (list) => (list || []).map(f => ({
        ...f,
        baggage: f.baggage || '2件',
        nextDay: !!(f.arrivalDate && f.date && f.arrivalDate !== f.date)
      }));
      const merged = [
        ...addDefaults(tourNotice.flightInfo.outbound),
        ...addDefaults(tourNotice.flightInfo.inbound),
      ];
      setFlights(merged);
    }
  }, [tourNotice]);

  const handleSave = async () => {
    try {
      setSaving(true);
      // 保存格式保持 outbound/inbound 结构，通知书渲染端兼容
      const flightInfo = { outbound: flights, inbound: [] };
      await axios.put(
        `${API_BASE}/tour-notices/tour/${tour.id}`,
        { flightInfo },
        { headers: AuthService.getAuthHeaders() }
      );

      message.success('航班信息保存成功');
      onRefresh();
    } catch (err) {
      message.error('保存失败: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const addFlight = () => {
    setFlights(prev => [...prev, { airline: '', flightNo: '', fromCity: '', toCity: '', date: '', time: '', arrivalTime: '', baggage: '2件' }]);
  };

  const removeFlight = (index) => {
    setFlights(prev => prev.filter((_, i) => i !== index));
  };

  const updateFlight = (index, field, value) => {
    setFlights(prev => prev.map((f, i) => {
      if (i !== index) return f;
      const updated = { ...f, [field]: value };
      // 如果修改了出发日期且已勾选+1天，自动更新到达日期
      if (field === 'date' && f.nextDay && value) {
        updated.arrivalDate = dayjs(value).add(1, 'day').format('YYYY-MM-DD');
      }
      return updated;
    }));
  };

  const handleFlightNoChange = (index, value) => {
    updateFlight(index, 'flightNo', value);
  };

  // 输入机场代码/名称时返回 AutoComplete options
  const buildAirportOptions = (input) => {
    const list = searchAirports(input);
    return list.map(a => ({
      value: formatAirportCn(a),
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span><strong>{a.code}</strong> · {a.cn}</span>
          <span style={{ color: '#999', fontSize: 12 }}>{a.en}</span>
        </div>
      ),
      airport: a
    }));
  };

  // 选中下拉项时同时填中文 + 英文
  const handleAirportSelect = (index, field, fieldEn, option) => {
    const a = option.airport;
    setFlights(prev => prev.map((f, i) => i === index ? {
      ...f,
      [field]: formatAirportCn(a),
      [fieldEn]: formatAirportEn(a)
    } : f));
  };

  // 文字输入：尝试按代码匹配，匹配上自动同步英文
  const handleAirportChange = (index, field, fieldEn, value) => {
    setFlights(prev => prev.map((f, i) => {
      if (i !== index) return f;
      const updated = { ...f, [field]: value };
      // 用户输入纯 3 字代码 → 自动展开
      const trimmed = String(value || '').trim().toUpperCase();
      if (/^[A-Z]{3}$/.test(trimmed)) {
        const a = findAirportByCode(trimmed);
        if (a) {
          updated[field] = formatAirportCn(a);
          updated[fieldEn] = formatAirportEn(a);
          return updated;
        }
      }
      // 否则尝试从输入中抽 (XXX) 后缀的代码 → 同步英文；找不到就清空英文
      const codeMatch = value && value.match(/\(([A-Z]{3})\)\s*$/i);
      if (codeMatch) {
        const a = findAirportByCode(codeMatch[1]);
        if (a) updated[fieldEn] = formatAirportEn(a);
      } else {
        updated[fieldEn] = '';
      }
      return updated;
    }));
  };

  const renderFlightRow = (flight, index) => (
    <div key={index} style={{ marginBottom: 12, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
      <Row gutter={8} align="middle">
        <Col span={3}>
          <Input placeholder="航班号" value={flight.flightNo} onChange={e => handleFlightNoChange(index, e.target.value)} size="small" />
        </Col>
        <Col span={3}>
          <Select
            placeholder="航司"
            value={flight.airline || undefined}
            onChange={(v) => updateFlight(index, 'airline', v || '')}
            size="small"
            style={{ width: '100%' }}
            showSearch
            allowClear
            optionFilterProp="label"
            options={airlineOptions}
            popupMatchSelectWidth={280}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '6px 0' }} />
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  style={{ paddingLeft: 8 }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setAddAirlineVisible(true)}
                >
                  新增航司
                </Button>
              </>
            )}
          />
        </Col>
        <Col span={3}>
          <AutoComplete
            placeholder="出发机场（输入 PVG）"
            value={flight.fromCity}
            options={buildAirportOptions(flight.fromCity)}
            onChange={(v) => handleAirportChange(index, 'fromCity', 'fromCityEn', v)}
            onSelect={(v, opt) => handleAirportSelect(index, 'fromCity', 'fromCityEn', opt)}
            size="small"
            style={{ width: '100%' }}
            allowClear
            popupMatchSelectWidth={360}
          />
        </Col>
        <Col span={3}>
          <AutoComplete
            placeholder="到达机场（输入 AKL）"
            value={flight.toCity}
            options={buildAirportOptions(flight.toCity)}
            onChange={(v) => handleAirportChange(index, 'toCity', 'toCityEn', v)}
            onSelect={(v, opt) => handleAirportSelect(index, 'toCity', 'toCityEn', opt)}
            size="small"
            style={{ width: '100%' }}
            allowClear
            popupMatchSelectWidth={360}
          />
        </Col>
        <Col span={3}>
          <DatePicker
            placeholder="日期"
            value={flight.date ? dayjs(flight.date) : null}
            defaultPickerValue={tour?.departureDate ? dayjs(tour.departureDate) : undefined}
            onChange={(d, ds) => updateFlight(index, 'date', ds)}
            size="small"
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={2}>
          <TimePicker
            placeholder="起飞"
            value={flight.time ? dayjs(flight.time, 'HH:mm') : null}
            onChange={(t, ts) => updateFlight(index, 'time', ts)}
            format="HH:mm"
            size="small"
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={2}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <TimePicker
              placeholder="到达"
              value={flight.arrivalTime ? dayjs(flight.arrivalTime, 'HH:mm') : null}
              onChange={(t, ts) => updateFlight(index, 'arrivalTime', ts)}
              format="HH:mm"
              size="small"
              style={{ flex: 1 }}
            />
            <Tooltip title="到达时间为第二天">
              <Checkbox
                checked={!!flight.nextDay}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked && flight.date) {
                    const nextDate = dayjs(flight.date).add(1, 'day').format('YYYY-MM-DD');
                    setFlights(prev => prev.map((f, i) => i === index ? { ...f, nextDay: true, arrivalDate: nextDate } : f));
                  } else {
                    setFlights(prev => prev.map((f, i) => i === index ? { ...f, nextDay: false, arrivalDate: flight.date || '' } : f));
                  }
                }}
                style={{ fontSize: 12 }}
              >
                <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>+1天</span>
              </Checkbox>
            </Tooltip>
          </div>
        </Col>
        <Col span={3}>
          <Input placeholder="行李额" value={flight.baggage} onChange={e => updateFlight(index, 'baggage', e.target.value)} size="small" />
        </Col>
        <Col span={1}>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeFlight(index)} />
        </Col>
      </Row>
    </div>
  );

  if (!tour?.destinationId) {
    return <Alert type="warning" message="请先在团组中设置目的地后再配置航班信息" showIcon />;
  }

  return (
    <div>
      <div style={{ marginBottom: 8, padding: '8px 12px', background: '#f6f8fa', borderRadius: 4, fontSize: 12, color: '#888' }}>
        输入航班号后自动匹配已有航线信息（航司、城市、时间），新航班号保存时自动记录
      </div>

      <Card title="航班信息" size="small" style={{ marginBottom: 16 }}
        extra={<Button size="small" icon={<PlusOutlined />} onClick={addFlight}>添加航班</Button>}
      >
        {flights.length === 0 ? (
          <Empty description="暂无航班信息" />
        ) : (
          flights.map((flight, index) => renderFlightRow(flight, index))
        )}
      </Card>

      <div style={{ textAlign: 'right' }}>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          保存航班信息
        </Button>
      </div>

      <Modal
        title="新增航司"
        open={addAirlineVisible}
        onCancel={() => setAddAirlineVisible(false)}
        onOk={handleAddAirline}
        confirmLoading={addingAirline}
        okText="添加"
      >
        <Form form={airlineForm} layout="vertical">
          <Form.Item name="name" label="航司名称" rules={[{ required: true, message: '请输入航司名称' }]}>
            <Input placeholder="如：新加坡航空 Singapore Airlines" />
          </Form.Item>
          <Form.Item name="code" label="IATA 代码" rules={[{ required: true, message: '请输入两位 IATA 代码' }]}>
            <Input placeholder="如：SQ" maxLength={3} style={{ textTransform: 'uppercase' }}
              onChange={(e) => airlineForm.setFieldsValue({ code: e.target.value.toUpperCase() })} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StepFlightInfo;
